import { combineReducers } from 'redux';

import EvidenceTypeEnum from 'redpoints-model-js-common/enumeration/EvidenceTypeEnum';
import EnforcementStatusEnum from 'redpoints-model-js/enumeration/EnforcementStatusEnum';
import NewLinkTypeEnum from 'redpoints-model-js/enumeration/NewLinkTypeEnum';
import Evidence from 'redpoints-model-js/model/incident/Evidence';

import HttpStatus from 'redpoints-connector-common/enumeration/HttpStatus';

import { ChannelLinkDataModelNameEnum } from 'redpoints-front-common/enumerations';
import { getLinkServiceModel } from 'redpoints-front-common/services';

import rpfdate from 'redpoints-front-date';

import accountPermissions from './accountPermissions';
import channelEvidenceTypes from './channelEvidenceTypes';
import contentLinkAccount from './contentLink/contentLinkAccount';
import contentLinkAccountId from './contentLink/contentLinkAccountId';
import contentLinkData from './contentLink/contentLinkData';
import contentLinkDataOriginal from './contentLink/contentLinkDataOriginal';
import contentLinkEvidences from './contentLink/contentLinkEvidences';
import contentLinkEvidencesOriginal from './contentLink/contentLinkEvidencesOriginal';
import contentLinkMetaData, * as fromContentLinkMetaData from './contentLink/contentLinkMetaData';
import contentLinkMonitorData from './contentLink/contentLinkMonitorData';
import contentLinkSave from './contentLink/contentLinkSave';
import contentLinkSeller from './contentLink/contentLinkSeller';
import contentLinkValidation, * as fromContentLinkValidation from './contentLink/contentLinkValidation';
import contentLinkRelatedAds from './contentLink/relatedAds';
import searchContentLinkAccount from './contentLink/searchContentLinkAccount';
import destinationLinks from './destinationLinks';
import detectionLinkAccount from './detectionLink/detectionLinkAccount';
import detectionLinkAccountId from './detectionLink/detectionLinkAccountId';
import detectionLinkData from './detectionLink/detectionLinkData';
import detectionLinkDataOriginal from './detectionLink/detectionLinkDataOriginal';
import detectionLinkEvidences from './detectionLink/detectionLinkEvidences';
import detectionLinkEvidencesOriginal from './detectionLink/detectionLinkEvidencesOriginal';
import detectionLinkMetaData, * as fromDetectionLinkMetaData from './detectionLink/detectionLinkMetaData';
import detectionLinkMonitorData from './detectionLink/detectionLinkMonitorData';
import detectionLinkSave from './detectionLink/detectionLinkSave';
import detectionLinkSeller from './detectionLink/detectionLinkSeller';
import detectionLinkValidation, * as fromDetectionLinkValidation from './detectionLink/detectionLinkValidation';
import detectionLinkRelatedAds from './detectionLink/relatedAds';
import searchDetectionLinkAccount from './detectionLink/searchDetectionLinkAccount';
import evidencePermissions from './evidencePermissions';
import incidentData from './incidentData';
import linkDataPermissions from './linkDataPermissions';
import sellerPermissions from './sellerPermissions';
import validation from './validation';

const incidentDetails = combineReducers({
  accountPermissions,
  channelEvidenceTypes,
  contentLinkAccount,
  contentLinkAccountId,
  contentLinkData,
  contentLinkDataOriginal,
  contentLinkEvidences,
  contentLinkEvidencesOriginal,
  contentLinkMetaData,
  contentLinkMonitorData,
  contentLinkRelatedAds,
  contentLinkSave,
  contentLinkSeller,
  contentLinkValidation,
  destinationLinks,
  detectionLinkAccount,
  detectionLinkAccountId,
  detectionLinkData,
  detectionLinkDataOriginal,
  detectionLinkEvidences,
  detectionLinkEvidencesOriginal,
  detectionLinkMetaData,
  detectionLinkMonitorData,
  detectionLinkRelatedAds,
  detectionLinkSave,
  detectionLinkSeller,
  detectionLinkValidation,
  evidencePermissions,
  incidentData,
  linkDataPermissions,
  searchContentLinkAccount,
  searchDetectionLinkAccount,
  sellerPermissions,
  validation,
});

export default incidentDetails;

const CLOSED_LINK_STATUSES = [
  EnforcementStatusEnum.ENFORCED.name,
  EnforcementStatusEnum.CLOSED.name,
];

const LINK_TYPE = {
  DETECTION_LINK: 'detectionLink',
  CONTENT_LINK: 'contentLink',
};

const getEnforcementStatusByLinkType = (state, linkType) => {
  const incident = getIncidentDetailsIncidentData(state);
  return incident[linkType]?.enforcementStatus;
};

const getEvidenceMandatoryByLinkType = (state, linkType) => {
  const originalEvidence =
    linkType === LINK_TYPE.DETECTION_LINK
      ? getIncidentDetailsDetectionLinkActiveOriginalEvidence(state)
      : getIncidentDetailsContentLinkActiveOriginalEvidence(state);

  return Boolean(originalEvidence.evidenceId);
};

const getActiveEvidence = (state, evidences, linkType) => {
  const enforcementStatus = getEnforcementStatusByLinkType(state, linkType);
  const evidenceTypeByEnforcementStatus = CLOSED_LINK_STATUSES.includes(
    enforcementStatus?.name
  )
    ? EvidenceTypeEnum.CLOSED.name
    : EvidenceTypeEnum.OPEN.name;
  const activeEvidence = evidences.find(
    ({ evidenceType }) => evidenceType.name === evidenceTypeByEnforcementStatus
  );

  return Object.assign(new Evidence(), activeEvidence);
};

export const getIncidentDetailsContentLinkActiveEvidence = state => {
  const contentLinkEvidences = getIncidentDetailsContentLinkEvidences(state);
  return getActiveEvidence(state, contentLinkEvidences, LINK_TYPE.CONTENT_LINK);
};

export const getIncidentDetailsContentLinkActiveOriginalEvidence = state => {
  const contentLinkEvidencesOriginal = getIncidentDetailsContentLinkEvidencesOriginal(
    state
  );
  return getActiveEvidence(
    state,
    contentLinkEvidencesOriginal,
    LINK_TYPE.CONTENT_LINK
  );
};

export const getIncidentDetailsContentLinkData = state =>
  state.reporting.incidentDetails.contentLinkData.response.data ?? {};

export const getIncidentDetailsHasContentLinkData = state =>
  Boolean(getIncidentDetailsIncidentData(state).contentLink);

export const getIncidentDetailsContentLinkDataOriginal = state =>
  state.reporting.incidentDetails.contentLinkDataOriginal.response.data ?? {};

export const getIncidentDetailsContentLinkDataIsLoading = state =>
  state.reporting.incidentDetails.contentLinkData.isLoading ||
  state.reporting.incidentDetails.contentLinkMetaData.isLoading;

export const getIncidentDetailsIsAdDetectionLink = state =>
  getIncidentDetailsIncidentData(state)?.detectionLink?.newLinkType ===
  NewLinkTypeEnum.AD;

export const getIncidentDetailsDestinationLinks = state =>
  state.reporting.incidentDetails.destinationLinks.response?.data ?? [];

export const getIncidentDetailsDetectionAdLinks = state =>
  state.reporting.incidentDetails.detectionLinkRelatedAds.response?.data ?? [];

export const getIncidentDetailsContentAdLinks = state =>
  state.reporting.incidentDetails.contentLinkRelatedAds.response?.data ?? [];

export const getIncidentDetailsContentLinkMetaData = state =>
  fromContentLinkMetaData.getIncidentDetailsContentLinkMetaData(
    state.reporting.incidentDetails.contentLinkMetaData
  );

export const getIncidentDetailsContentLinkEvidenceIsMandatory = state =>
  getEvidenceMandatoryByLinkType(state, LINK_TYPE.CONTENT_LINK);

export const getIncidentDetailsContentLinkEnforcementStatus = state =>
  getEnforcementStatusByLinkType(state, LINK_TYPE.CONTENT_LINK);

export const getIncidentDetailsContentLinkEvidences = state =>
  state.reporting.incidentDetails.contentLinkEvidences.response.data ?? [];

export const getIncidentDetailsContentLinkEvidencesOriginal = state =>
  state.reporting.incidentDetails.contentLinkEvidencesOriginal.response.data ??
  [];

export const getIncidentDetailsContentLinkEvidencesIsLoading = state =>
  state.reporting.incidentDetails.contentLinkEvidences.isLoading ||
  state.reporting.incidentDetails.evidencePermissions.isLoading;

export const getIncidentDetailsContentLinkIsValid = state =>
  fromContentLinkValidation.getIncidentDetailsContentLinkIsValid(
    state.reporting.incidentDetails.contentLinkValidation
  );

export const getIncidentDetailsContentLinkIsValidating = state =>
  fromContentLinkValidation.getIncidentDetailsContentLinkIsValidating(
    state.reporting.incidentDetails.contentLinkValidation,
    getIncidentDetailsForceValidation(state)
  );

export const getIncidentDetailsContentLinkInitialLinkAccountId = state =>
  state.reporting.incidentDetails.contentLinkAccountId;

export const getIncidentDetailsContentLinkValidationInitialState = state => {
  const contentLinkMetadata = getIncidentDetailsContentLinkMetaData(state);

  return fromContentLinkValidation.getIncidentDetailsContentLinkValidationInitialState(
    {
      hasEvidence: getIncidentDetailsContentLinkEvidenceIsMandatory(state),
      hasLinkMetadataAccount: Boolean(
        contentLinkMetadata?.get(ChannelLinkDataModelNameEnum.ACCOUNT.name)
      ),
      hasLinkMetadataCommon: Boolean(
        contentLinkMetadata?.get(
          ChannelLinkDataModelNameEnum.COMMONLINKDATA.name
        )
      ),
      hasLinkMetadataLinkImage: Boolean(
        contentLinkMetadata?.get(ChannelLinkDataModelNameEnum.LINKIMAGE.name)
      ),
      hasLinkMetadataLinkPrice: Boolean(
        contentLinkMetadata?.get(ChannelLinkDataModelNameEnum.LINKPRICE.name)
      ),
      hasLinkMetadataMultimediaFile: Boolean(
        contentLinkMetadata?.get(
          ChannelLinkDataModelNameEnum.MULTIMEDIAFILELINKDATA.name
        )
      ),
      hasLinkMetadataOfferListing: Boolean(
        contentLinkMetadata?.get(
          ChannelLinkDataModelNameEnum.OFFERLISTINGLINKDATA.name
        )
      ),
      hasLinkMetadataOverallMultimedia: Boolean(
        contentLinkMetadata?.get(
          ChannelLinkDataModelNameEnum.OVERALLMULTIMEDIALINKDATA.name
        )
      ),
      hasLinkMetadataProductListing: Boolean(
        contentLinkMetadata?.get(
          ChannelLinkDataModelNameEnum.PRODUCTLISTINGLINKDATA.name
        )
      ),
      hasLinkMetadataSeller: Boolean(
        contentLinkMetadata?.get(ChannelLinkDataModelNameEnum.SELLER.name)
      ),
      hasLinkMetadataSocialMedia: Boolean(
        contentLinkMetadata?.get(
          ChannelLinkDataModelNameEnum.SOCIALMEDIALINKDATA.name
        )
      ),
    }
  );
};

export const getIncidentDetailsContentLinkAccountModified = state =>
  state.reporting.incidentDetails.contentLinkAccount;

export const getIncidentDetailsContentLinkAccountOriginal = state =>
  getIncidentDetailsIncidentData(state).contentLink?.account;

export const getIncidentDetailsContentLinkAccountId = state =>
  state.reporting.incidentDetails.contentLinkAccountId;

export const getIncidentDetailsContentLinkSearchAccountStatus = state =>
  state.reporting.incidentDetails.searchContentLinkAccount.response.status;

export const getIncidentDetailsContentLinkSearchAccountIsLoading = state =>
  state.reporting.incidentDetails.searchContentLinkAccount.isLoading;

export const getIncidentDetailsContentLinkSeller = state =>
  state.reporting.incidentDetails.contentLinkSeller;

export const getIncidentDetailsContentLinkSaveServiceModel = state => {
  const incidentData = getIncidentDetailsIncidentData(state);
  const linkId = incidentData.contentLink?.contentLinkId ?? null;
  const assetId = incidentData.asset?.assetId ?? null;

  return getLinkServiceModel({
    assetId,
    initialLinkAccountId: getIncidentDetailsContentLinkInitialLinkAccountId(
      state
    ),
    linkId,
    modifiedAccount: getIncidentDetailsContentLinkAccountModified(state),
    modifiedEvidence: getIncidentDetailsContentLinkActiveEvidence(state),
    modifiedLinkData: getIncidentDetailsContentLinkData(state),
    modifiedSeller: getIncidentDetailsContentLinkSeller(state),
    originalAccount: getIncidentDetailsContentLinkAccountOriginal(state),
    originalEvidence: getIncidentDetailsContentLinkActiveOriginalEvidence(
      state
    ),
    originalLinkData: getIncidentDetailsContentLinkDataOriginal(state),
    originalSeller: incidentData.contentLink?.seller,
  });
};

export const getIncidentDetailsDetectionLinkActiveEvidence = state => {
  const detectionLinkEvidences = getIncidentDetailsDetectionLinkEvidences(
    state
  );
  return getActiveEvidence(
    state,
    detectionLinkEvidences,
    LINK_TYPE.DETECTION_LINK
  );
};

export const getIncidentDetailsDetectionLinkActiveOriginalEvidence = state => {
  const detectionLinkEvidencesEvidencesOriginal = getIncidentDetailsDetectionLinkEvidencesOriginal(
    state
  );
  return getActiveEvidence(
    state,
    detectionLinkEvidencesEvidencesOriginal,
    LINK_TYPE.DETECTION_LINK
  );
};

export const getIncidentDetailsDetectionLinkData = state =>
  state.reporting.incidentDetails.detectionLinkData.response.data ?? {};

export const getIncidentDetailsDetectionLinkDataOriginal = state =>
  state.reporting.incidentDetails.detectionLinkDataOriginal.response.data ?? {};

export const getIncidentDetailsDetectionLinkDataIsLoading = state =>
  state.reporting.incidentDetails.detectionLinkData.isLoading ||
  state.reporting.incidentDetails.detectionLinkMetaData.isLoading;

export const getIncidentDetailsDetectionLinkEnforcementStatus = state =>
  getEnforcementStatusByLinkType(state, LINK_TYPE.DETECTION_LINK);

export const getIncidentDetailsEvidencePermissions = state =>
  state.reporting.incidentDetails.evidencePermissions.response.status ===
  HttpStatus.NO_CONTENT.value;

export const getIncidentDetailsDetectionLinkEvidenceIsMandatory = state =>
  getEvidenceMandatoryByLinkType(state, LINK_TYPE.DETECTION_LINK);

export const getIncidentDetailsDetectionLinkMetaData = state =>
  fromDetectionLinkMetaData.getIncidentDetailsDetectionLinkMetaData(
    state.reporting.incidentDetails.detectionLinkMetaData
  );

export const getIncidentDetailsDetectionLinkEvidences = state =>
  state.reporting.incidentDetails.detectionLinkEvidences.response.data ?? [];

export const getIncidentDetailsDetectionLinkEvidencesOriginal = state =>
  state.reporting.incidentDetails.detectionLinkEvidencesOriginal.response
    .data ?? [];

export const getIncidentDetailsDetectionLinkEvidencesIsLoading = state =>
  state.reporting.incidentDetails.detectionLinkEvidences.isLoading ||
  state.reporting.incidentDetails.evidencePermissions.isLoading;

export const getIncidentDetailsIncidentData = state =>
  state.reporting.incidentDetails.incidentData.response?.data ?? {};

export const getIncidentDetailsDataIsLoading = state =>
  state.reporting.incidentDetails.incidentData.isLoading;

export const getIncidentDetailsLinkDataPermissions = state =>
  state.reporting.incidentDetails.linkDataPermissions.response.status ===
  HttpStatus.NO_CONTENT.value;

export const getIncidentDetailsForceValidation = state =>
  state.reporting.incidentDetails.validation.force;

export const getIncidentDetailsDetectionLinkIsValid = state =>
  fromDetectionLinkValidation.getIncidentDetailsDetectionLinkIsValid(
    state.reporting.incidentDetails.detectionLinkValidation
  );

export const getIncidentDetailsDetectionLinkIsValidating = state =>
  fromDetectionLinkValidation.getIncidentDetailsDetectionLinkIsValidating(
    state.reporting.incidentDetails.detectionLinkValidation,
    getIncidentDetailsForceValidation(state)
  );

export const getIncidentDetailsDetectionLinkInitialLinkAccountId = state =>
  state.reporting.incidentDetails.detectionLinkAccountId;

export const getIncidentDetailsDetectionLinkValidationInitialState = state => {
  const detectionLinkMetadata = getIncidentDetailsDetectionLinkMetaData(state);

  return fromDetectionLinkValidation.getIncidentDetailsDetectionLinkValidationInitialState(
    {
      hasEvidence: getIncidentDetailsDetectionLinkEvidenceIsMandatory(state),
      hasLinkMetadataAccount: Boolean(
        detectionLinkMetadata?.get(ChannelLinkDataModelNameEnum.ACCOUNT.name)
      ),
      hasLinkMetadataCommon: Boolean(
        detectionLinkMetadata?.get(
          ChannelLinkDataModelNameEnum.COMMONLINKDATA.name
        )
      ),
      hasLinkMetadataLinkImage: Boolean(
        detectionLinkMetadata?.get(ChannelLinkDataModelNameEnum.LINKIMAGE.name)
      ),
      hasLinkMetadataLinkPrice: Boolean(
        detectionLinkMetadata?.get(ChannelLinkDataModelNameEnum.LINKPRICE.name)
      ),
      hasLinkMetadataMultimediaFile: Boolean(
        detectionLinkMetadata?.get(
          ChannelLinkDataModelNameEnum.MULTIMEDIAFILELINKDATA.name
        )
      ),
      hasLinkMetadataOfferListing: Boolean(
        detectionLinkMetadata?.get(
          ChannelLinkDataModelNameEnum.OFFERLISTINGLINKDATA.name
        )
      ),
      hasLinkMetadataOverallMultimedia: Boolean(
        detectionLinkMetadata?.get(
          ChannelLinkDataModelNameEnum.OVERALLMULTIMEDIALINKDATA.name
        )
      ),
      hasLinkMetadataProductListing: Boolean(
        detectionLinkMetadata?.get(
          ChannelLinkDataModelNameEnum.PRODUCTLISTINGLINKDATA.name
        )
      ),
      hasLinkMetadataSeller: Boolean(
        detectionLinkMetadata?.get(ChannelLinkDataModelNameEnum.SELLER.name)
      ),
      hasLinkMetadataSocialMedia: Boolean(
        detectionLinkMetadata?.get(
          ChannelLinkDataModelNameEnum.SOCIALMEDIALINKDATA.name
        )
      ),
    }
  );
};

export const getIncidentDetailsDetectionLinkAccountModified = state =>
  state.reporting.incidentDetails.detectionLinkAccount;

export const getIncidentDetailsDetectionLinkAccountOriginal = state =>
  getIncidentDetailsIncidentData(state).detectionLink?.account;

export const getIncidentDetailsDetectionLinkSearchAccountStatus = state =>
  state.reporting.incidentDetails.searchDetectionLinkAccount.response.status;

export const getIncidentDetailsDetectionLinkSearchAccountIsLoading = state =>
  state.reporting.incidentDetails.searchDetectionLinkAccount.isLoading;

export const getIncidentDetailsDetectionLinkSeller = state =>
  state.reporting.incidentDetails.detectionLinkSeller;

export const getIncidentDetailsDetectionLinkSaveServiceModel = state => {
  const incidentData = getIncidentDetailsIncidentData(state);
  const linkId = incidentData.detectionLink?.detectionLinkId ?? null;
  const assetId = incidentData.asset?.assetId ?? null;

  return getLinkServiceModel({
    assetId,
    initialLinkAccountId: getIncidentDetailsDetectionLinkInitialLinkAccountId(
      state
    ),
    linkId,
    modifiedAccount: getIncidentDetailsDetectionLinkAccountModified(state),
    modifiedEvidence: getIncidentDetailsDetectionLinkActiveEvidence(state),
    modifiedLinkData: getIncidentDetailsDetectionLinkData(state),
    modifiedSeller: getIncidentDetailsDetectionLinkSeller(state),
    originalAccount: getIncidentDetailsDetectionLinkAccountOriginal(state),
    originalEvidence: getIncidentDetailsDetectionLinkActiveOriginalEvidence(
      state
    ),
    originalLinkData: getIncidentDetailsDetectionLinkDataOriginal(state),
    originalSeller: incidentData.detectionLink?.seller,
  });
};

export const getIncidentDetailsSaveLinksIsLoading = state =>
  state.reporting.incidentDetails.contentLinkSave.isLoading ||
  state.reporting.incidentDetails.detectionLinkSave.isLoading;

export const getIncidentDetailsSaveLinksIsSuccess = state => {
  const {
    contentLinkSave,
    detectionLinkSave,
  } = state.reporting.incidentDetails;

  const hasContentLink = getIncidentDetailsHasContentLinkData(state);

  const contentLinkSuccess =
    !hasContentLink ||
    (contentLinkSave.response.status && !contentLinkSave.error?.error);
  const detectionLinkSuccess =
    detectionLinkSave.response.status && !detectionLinkSave.error?.error;

  return Boolean(contentLinkSuccess && detectionLinkSuccess);
};

export const getIncidentDetailsSellerPermissions = state =>
  state.reporting.incidentDetails.sellerPermissions.response.status ===
  HttpStatus.NO_CONTENT.value;

export const getIncidentDetailsAccountPermissions = state =>
  state.reporting.incidentDetails.accountPermissions.response.status ===
  HttpStatus.NO_CONTENT.value;

export const getIncidentDetailsDetectionLinkMonitorData = state => {
  const lastMonitorRequest =
    state.reporting.incidentDetails.detectionLinkMonitorData.response?.data
      ?.lastRequest;
  const lastMonitorResponse =
    state.reporting.incidentDetails.detectionLinkMonitorData.response?.data
      ?.lastMonitorResponseDate;
  return {
    lastMonitorRequest: lastMonitorRequest
      ? rpfdate(lastMonitorRequest).format(rpfdate.MASKS.DATETIMEWITHSECONDS)
      : null,
    lastMonitorResponse: lastMonitorResponse
      ? rpfdate(lastMonitorResponse).format(rpfdate.MASKS.DATETIMEWITHSECONDS)
      : null,
  };
};

export const getIncidentDetailsContentLinkMonitorData = state => {
  const lastMonitorRequest =
    state.reporting.incidentDetails.contentLinkMonitorData.response?.data
      ?.lastRequest;
  const lastMonitorResponse =
    state.reporting.incidentDetails.contentLinkMonitorData.response?.data
      ?.lastMonitorResponseDate;
  return {
    lastMonitorRequest: lastMonitorRequest
      ? rpfdate(lastMonitorRequest).format(rpfdate.MASKS.DATETIMEWITHSECONDS)
      : null,
    lastMonitorResponse: lastMonitorResponse
      ? rpfdate(lastMonitorResponse).format(rpfdate.MASKS.DATETIMEWITHSECONDS)
      : null,
  };
};
